import { countries, type Country } from "@redotech/locale/countries";
import { NonZeroValueExchangeStrategy } from "@redotech/redo-common-model/exchange";
import { CustomerPortalVersion } from "@redotech/redo-customer-portal-common/customer-portal-version";
import { ShopifyExchangePaymentStrategy } from "@redotech/redo-model/shopify";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput, LabelSize } from "@redotech/redo-web/labeled-input";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { arrayEqual } from "@redotech/util/equal";
import { memo } from "react";

export const returnsGeneralForm = groupInput({
  enabled: input<boolean>(),
  customerPortalVersion: input<CustomerPortalVersion>(),
  refundAllReturnedItems: input<boolean>(),
  skipExchangeRefunds: input<boolean>(),
  allowMultiOrderReturns: input<boolean>(),
  multiOrderReturnLimit: input<string>(),
  chargeMerchantForDamagedOrWrongProductReturnLabels: input<boolean>(),
  enableCustomerPortal: input<boolean>(),
  buyYourOwnLabel: input<boolean>(),
  nonZeroValueExchangeStrategy: input<NonZeroValueExchangeStrategy>(),
  agentReviewEnabled: input<boolean>(),
  agentChatEnabled: input<boolean>(),
  exchangePaymentStrategy: input<ShopifyExchangePaymentStrategy | null>(),
  shopifyDeferReturnItemProcessing: input<boolean>(),
  orderSkuMetafieldKey: input<string | undefined>(),
  dutyDrawbackEnabled: input<boolean>(),
  dutyDrawbackEligibleCountries: input<Country[]>({
    equal: arrayEqual((a, b) => a.code === b.code),
  }),
});

export type ReturnsGeneralForm = InputSchema.Input<typeof returnsGeneralForm>;

export type ReturnsGeneralValue = InputSchema.Value<typeof returnsGeneralForm>;

export const returnsGeneralDefault: ReturnsGeneralValue = {
  enabled: true,
  customerPortalVersion: CustomerPortalVersion.V4_0,
  refundAllReturnedItems: false,
  skipExchangeRefunds: false,
  allowMultiOrderReturns: false,
  multiOrderReturnLimit: "0",
  chargeMerchantForDamagedOrWrongProductReturnLabels: true,
  enableCustomerPortal: true,
  buyYourOwnLabel: false,
  nonZeroValueExchangeStrategy: NonZeroValueExchangeStrategy.NONE,
  agentReviewEnabled: false,
  agentChatEnabled: false,
  exchangePaymentStrategy: null,
  shopifyDeferReturnItemProcessing: false,
  orderSkuMetafieldKey: undefined,
  dutyDrawbackEnabled: false,
  dutyDrawbackEligibleCountries: [],
};

const EXCHANGE_PAYMENT_STRATEGY_OPTIONS = [
  {
    id: ShopifyExchangePaymentStrategy.DRAFT_ORDER,
    type: "text" as const,
    text: "Draft order",
    value: ShopifyExchangePaymentStrategy.DRAFT_ORDER,
  },
  {
    id: ShopifyExchangePaymentStrategy.STRIPE,
    type: "text" as const,
    text: "Stripe",
    value: ShopifyExchangePaymentStrategy.STRIPE,
  },
];

export const ReturnsGeneralCard = memo(function ReturnsGeneralCard({
  input,
  hasShopifyIntegration,
}: {
  input: ReturnsGeneralForm;
  hasShopifyIntegration: boolean;
}) {
  const {
    refundAllReturnedItems,
    skipExchangeRefunds,
    allowMultiOrderReturns,
    multiOrderReturnLimit,
    chargeMerchantForDamagedOrWrongProductReturnLabels,
    enableCustomerPortal,
    buyYourOwnLabel,
    agentReviewEnabled,
    agentChatEnabled,
    exchangePaymentStrategy,
    shopifyDeferReturnItemProcessing,
    orderSkuMetafieldKey,
    dutyDrawbackEnabled,
    dutyDrawbackEligibleCountries,
  } = input.inputs;

  return (
    <Flex dir="column" gap="3xl">
      <RedoToggle
        description="Allow customers to return through the customer portal"
        label="Enable customer portal"
        setValue={enableCustomerPortal.setValue}
        size="sm"
        value={enableCustomerPortal.value ?? false}
      />
      <RedoToggle
        description="A single return can be created from multiple orders"
        label="Allow multi order returns (CommentSold only)"
        setValue={allowMultiOrderReturns.setValue}
        size="sm"
        value={allowMultiOrderReturns.value ?? false}
      />
      {allowMultiOrderReturns.value && (
        <RedoTextInput
          description=""
          inputTypeHint="number"
          label="Multi Order Returns Day Range"
          min={0}
          setValue={multiOrderReturnLimit.setValue}
          value={multiOrderReturnLimit.value ?? ""}
        />
      )}
      {refundAllReturnedItems.value && (
        <RedoToggle
          description='Skip creating $0 Shopify refunds for exchanges even when "Always create refund" is enabled. (Only if there is no refund and no store credit)'
          label="Skip exchange refunds"
          setValue={skipExchangeRefunds.setValue}
          size="sm"
          value={skipExchangeRefunds.value ?? false}
        />
      )}
      <RedoToggle
        label="Charge Merchants for damaged or wrong product return labels"
        setValue={chargeMerchantForDamagedOrWrongProductReturnLabels.setValue}
        size="sm"
        value={
          chargeMerchantForDamagedOrWrongProductReturnLabels.value ?? false
        }
      />
      <RedoToggle
        description="Changes text during international green returns to prompt the shopper to buy their own label."
        label="Enable Buy Your Own Label"
        setValue={buyYourOwnLabel.setValue}
        size="sm"
        value={buyYourOwnLabel.value ?? false}
      />
      <RedoToggle
        description="Show AI risk assessment and recommendations for returns"
        label="Enable AI return review"
        setValue={agentReviewEnabled.setValue}
        size="sm"
        value={agentReviewEnabled.value ?? false}
      />
      <RedoToggle
        description="Enable AI chat functionality for customer return inquiries"
        label="Enable AI chat for returns"
        setValue={agentChatEnabled.setValue}
        size="sm"
        value={agentChatEnabled.value ?? false}
      />
      {hasShopifyIntegration && (
        <LabeledInput
          description="Determines how exchange orders are paid for in Shopify."
          label="Exchange payment strategy"
          size={LabelSize.SMALL}
        >
          <RedoSingleSelectDropdownInput<ShopifyExchangePaymentStrategy>
            options={EXCHANGE_PAYMENT_STRATEGY_OPTIONS}
            optionSelected={({ item }) => {
              exchangePaymentStrategy.setValue(item.value);
            }}
            selectedItem={EXCHANGE_PAYMENT_STRATEGY_OPTIONS.find(
              (o) => o.value === exchangePaymentStrategy.value,
            )}
          />
        </LabeledInput>
      )}
      {hasShopifyIntegration &&
        exchangePaymentStrategy.value ===
          ShopifyExchangePaymentStrategy.STRIPE && (
          <RedoToggle
            description="When enabled, only exchange items are processed in Shopify at return creation time. Return items are deferred until later. Only applies to instant exchanges"
            label="Defer return item processing"
            setValue={shopifyDeferReturnItemProcessing.setValue}
            size="sm"
            value={shopifyDeferReturnItemProcessing.value ?? false}
          />
        )}
      <RedoTextInput
        description="Override the SKU on return items using a Shopify order metafield. The metafield value should be a JSON mapping of {line_item_id: sku}."
        label="Order SKU Metafield Key"
        placeholder="custom.real_skus"
        setValue={(value: string) =>
          orderSkuMetafieldKey.setValue(value || undefined)
        }
        value={orderSkuMetafieldKey.value ?? ""}
      />
      <RedoToggle
        description="Credit duties collected on returned line items to the merchant's next Redo invoice when the return originates from a country in the eligibility list. Independent of customer-facing duty refunds."
        label="Credit duty drawback to merchant invoice"
        setValue={dutyDrawbackEnabled.setValue}
        size="sm"
        value={dutyDrawbackEnabled.value ?? false}
      />
      {dutyDrawbackEnabled.value && (
        <LabeledInput
          description="Returns shipped from these countries are eligible for an automatic duty drawback credit on the merchant's invoice."
          label="Duty drawback eligible countries"
          size={LabelSize.SMALL}
        >
          <Autocomplete
            disableCloseOnSelect
            getLabel={(country) => country.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            multiple
            options={countries}
            placeholder="Search countries..."
            value={dutyDrawbackEligibleCountries.value ?? []}
            valueChange={(newValue) =>
              dutyDrawbackEligibleCountries.setValue(newValue || [])
            }
          />
        </LabeledInput>
      )}
    </Flex>
  );
});
